/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { collection, DocumentData } from 'firebase/firestore';
import { useState, useEffect, useMemo } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { db } from '../../firebase-setup/firebase';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import theme from '../../theme';

function DrugLookupFYM() {
  const [medications, loadingMedications] = useCollection(
    collection(db, 'medications'),
    { snapshotListenOptions: { includeMetadataChanges: false } }
  );
  const [diagnosisData, loadingDiagnosisList] = useCollection(
    collection(db, 'diagnosis'),
    { snapshotListenOptions: { includeMetadataChanges: false } }
  );
  const [medicationsList, setMedicationsList] = useState<DocumentData[] | null>(
    null
  );

  const [filteredMedicationsList, setFilteredMedicationsList] = useState<
    DocumentData[] | null
  >(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [disease, setDisease] = useState<string>('');

  const sortedDiseaseList = useMemo(() => {
    if (loadingDiagnosisList || !diagnosisData) return null;
    const diseaseList = diagnosisData.docs
      .map((doc) => doc.data())
      .sort((a, b) => (a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1));
    diseaseList.unshift({ name: 'All' });
    return diseaseList;
  }, [diagnosisData, loadingDiagnosisList]);

  // Filter medications list based on the search query
  useEffect(() => {
    if (!searchQuery || searchQuery === '') {
      setFilteredMedicationsList(medicationsList);
    } else {
      const filteredList = medicationsList?.filter(
        (medication) =>
          medication.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          medication.genericName
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
      );
      setFilteredMedicationsList(filteredList || null);
    }
  }, [searchQuery, medicationsList]);
  // Filter medications list based on the search query
  useEffect(() => {
    if (!disease || disease === 'All') {
      setFilteredMedicationsList(medicationsList);
    } else {
      const filteredList = medicationsList?.filter((medication) => {
        if (medication.diagnosis.includes(disease)) {
          return medication;
        }
      });
      setFilteredMedicationsList(filteredList || null);
    }
  }, [disease, medicationsList]);

  const filterMedicationsOptions = (options: any[], state: any) => {
    const inputValue = state.inputValue;
    return options.filter((option) => {
      if (
        option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.genericName.toLowerCase().includes(inputValue.toLowerCase())
      ) {
        return option;
      }
      return false;
    });
  };

  const [listHeight, setListHeight] = useState(0);

  useEffect(() => {
    if (listHeight < 20000) {
      const stackElement = document.getElementById(`medlist`);
      if (stackElement) {
        setListHeight(
          120 + Number(stackElement.getBoundingClientRect().height)
        );
      }
    }
  }, [loadingMedications, medicationsList, listHeight]);
  useEffect(() => {
    window.parent.postMessage(
      {
        type: 'resize',
        value: listHeight,
      },
      '*'
    );
  }, [listHeight]);

  useEffect(() => {
    if (loadingMedications || !medications) return;
    let orderdList = medications.docs.map((doc) => doc.data());
    orderdList = orderdList.sort((a, b) => {
      if (a.name > b.name) return 1;
      if (a.name < b.name) return -1;
      return 0;
    });
    setMedicationsList(orderdList);
  }, [loadingMedications, medications]);

  return (
    <>
      {medicationsList && medicationsList.length > 89 ? (
        <Stack
          id={'medlist'}
          spacing={2}
          alignItems={'center'}
          justifyContent={'center'}
          width={'100%'}
          sx={{
            paddingTop: {
              sm: 2,
              md: 8,
            },
          }}>
          <Typography
            variant="h1"
            textAlign={'center'}
            fontSize={{
              sm: '3rem',
              md: '4.8rem',
            }}
            color={'#153b61'}>
            Medication List
          </Typography>
          <Stack
            spacing={2}
            alignItems={'stretch'}
            width={'100%'}
            className="container"
            sx={{
              overflowX: 'clip',
            }}>
            <Stack width={'100%'} alignItems={'center'}>
              <Stack
                spacing={2}
                maxWidth={{ xs: '100%', sm: '60%' }}
                p={2}
                borderRadius={2}>
                <Button
                  sx={{
                    alignSelf: 'center',
                    '&:hover': {
                      backgroundColor: 'white',
                      color: theme.palette.primary.main,
                      textAlign: 'center',
                    },
                  }}
                  variant="contained"
                  color="primary"
                  target="_blank"
                  href="https://cdn.transparentpricerx.com/Medlist%20TPRX%20Oct%202025.pdf">
                  Download Medication List PDF
                </Button>
              </Stack>
            </Stack>

            <Grid container sx={{ width: '100%' }} spacing={2}>
              <Grid item xs={12} sm={6} key={'diagnosis grid'}>
                <TextField
                  key={'diagnosis'}
                  fullWidth
                  select
                  SelectProps={{
                    MenuProps: {
                      style: {
                        maxHeight: 500,
                      },
                    },
                  }}
                  name={'product'}
                  value={disease || ''}
                  label={'Filter by disease'}
                  onChange={(e) => setDisease(e.target.value)}
                  variant="outlined">
                  {sortedDiseaseList &&
                    sortedDiseaseList.map((diagnosis) => (
                      <MenuItem key={diagnosis.name} value={diagnosis.name}>
                        {diagnosis.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} key={'search medications grid'}>
                <Autocomplete
                  filterOptions={filterMedicationsOptions}
                  disablePortal
                  options={medicationsList}
                  value={
                    medicationsList.find(
                      (option) => option.name === searchQuery
                    ) || null
                  }
                  getOptionLabel={(option: any) =>
                    `${option.name} (${option.genericName})`
                  }
                  onChange={(_e, value: any) => {
                    setSearchQuery(!value ? '' : value.name);
                  }}
                  renderOption={(props, option: any) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Stack
                        key={key}
                        component="li"
                        {...optionProps}
                        direction={'row'}>
                        <Stack direction={'column'} mr={'auto'}>
                          <Typography variant="body1">{option.name}</Typography>
                          <Typography variant="body2">
                            ({option.genericName})
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            backgroundColor: option.brand
                              ? theme.palette.primary.dark
                              : theme.palette.primary.main,
                            color: theme.palette.common.white,
                            padding: '0.2rem 0.5rem',
                            borderRadius: '0.5rem',
                            marginLeft: '2rem',
                          }}>
                          {option.brand ? 'Brand' : 'Generic'}
                        </Typography>
                      </Stack>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      fullWidth
                      value={searchQuery || null}
                      name={'medicationName'}
                      label={'Select Medication'}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                overflowX: 'auto',
                xs: { width: '100svw' },
              }}>
              <Grid
                container
                borderBottom={1}
                pb={1}
                wrap="nowrap"
                width={{ xs: '66rem', sm: 'auto' }}>
                <Grid
                  item
                  xs={3}
                  sx={{
                    xs: {
                      flex: 1,
                      maxWidth: 'unset',
                      width: '20rem',
                      marginRight: '2rem',
                    },
                  }}>
                  <Typography variant="h3" color={'#153b61'} noWrap>
                    Medication Name
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{
                    xs: {
                      flex: 1,
                      maxWidth: 'unset',
                      width: '20rem',
                      marginRight: '2rem',
                    },
                  }}>
                  <Typography variant="h3" color={'#153b61'}>
                    Strength
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{
                    xs: {
                      flex: 1,
                      maxWidth: 'unset',
                      width: '20rem',
                      marginRight: '2rem',
                    },
                  }}>
                  <Typography variant="h3" color={'#153b61'}>
                    Price
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={{
                    xs: {
                      flex: 1,
                      maxWidth: 'unset',
                      width: '20rem',
                      marginRight: '2rem',
                    },
                  }}>
                  <Typography variant="h3" color={'#153b61'}>
                    Disease
                  </Typography>
                </Grid>
              </Grid>
              {filteredMedicationsList?.map((medication, index) => (
                <Grid
                  key={index}
                  container
                  borderBottom={1}
                  pb={1}
                  wrap="nowrap"
                  width={{ xs: '66rem', sm: '100%' }}>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      xs: {
                        flex: 1,
                        maxWidth: 'unset',
                        width: '20rem',
                        marginRight: '2rem',
                      },
                      paddingRight: '2rem',
                    }}>
                    <Stack
                      direction={'row'}
                      justifyContent={'space-between'}
                      alignItems={'center'}>
                      <Stack>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          color={'#153b61'}>
                          {medication.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            marginTop: 0,
                          }}>
                          {medication.genericName}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          backgroundColor: medication.brand
                            ? theme.palette.primary.dark
                            : theme.palette.primary.main,
                          color: theme.palette.common.white,
                          padding: '0.2rem 0.5rem',
                          borderRadius: '0.5rem',
                          marginLeft: '2rem',
                        }}>
                        {medication.brand ? 'Brand' : 'Generic'}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      xs: {
                        flex: 1,
                        maxWidth: 'unset',
                        width: '20rem',
                        marginRight: '2rem',
                      },
                    }}>
                    {medication.options.map((option: any, index: number) => (
                      <Grid key={index}>
                        <Typography variant="body2">
                          {option.strength}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      xs: {
                        flex: 1,
                        maxWidth: 'unset',
                        width: '20rem',
                        marginRight: '2rem',
                      },
                    }}>
                    {medication.options.map((option: any, index: number) => (
                      <Grid key={index}>
                        <Typography variant="body2">
                          {option.price === 'No cost'
                            ? `${option.price}, refilled every 90 days`
                            : `${option.price}`}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    sx={{
                      xs: {
                        flex: 1,
                        maxWidth: 'unset',
                        width: '20rem',
                        marginRight: '2rem',
                      },
                    }}>
                    <Grid key={index}>
                      <Typography variant="body2">
                        {medication.diagnosis}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Stack>
        </Stack>
      ) : (
        <div className="loader-s">
          <div className="loader" />
        </div>
      )}
    </>
  );
}
export default DrugLookupFYM;
